if (!customElements.get('finch-trustpilot-reviews')) {
  class FinchTrustpilotReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.track = this.querySelector('[data-ftr-track]');
      this.cards = Array.from(this.querySelectorAll('[data-ftr-card]'));
      this.previousButton = this.querySelector('[data-ftr-previous]');
      this.nextButton = this.querySelector('[data-ftr-next]');
      this.status = this.querySelector('[data-ftr-status]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      this.announcementTimer = null;
      this.scrollFrame = null;

      if (!this.track || this.cards.length === 0) return;

      const { signal } = this.controller;
      this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal });
      this.track.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.handleLayoutChange());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.handleLayoutChange(), { passive: true, signal });
      }

      this.formatRelativeTimes();
      this.dataset.initialized = 'true';
      this.update();
      this.announceStatus();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.announcementTimer) clearTimeout(this.announcementTimer);
      this.announcementTimer = null;
      this.scrollFrame = null;
      delete this.dataset.initialized;
    }

    handleScroll() {
      this.scheduleUpdate();
      this.scheduleAnnouncement();
    }

    handleLayoutChange() {
      this.scheduleUpdate();
      this.scheduleAnnouncement();
    }

    scheduleUpdate() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.update();
      });
    }

    scheduleAnnouncement() {
      if (this.announcementTimer) clearTimeout(this.announcementTimer);

      this.announcementTimer = setTimeout(() => {
        this.announcementTimer = null;
        this.update();
        this.announceStatus();
      }, 160);
    }

    announceStatus() {
      if (!this.status) return;

      const visibleStart = this.dataset.visibleStart || '1';
      const visibleEnd = this.dataset.visibleEnd || visibleStart;
      const message = `Reviews ${visibleStart}–${visibleEnd} van ${this.cards.length}`;

      if (this.status.textContent !== message) {
        this.status.textContent = message;
      }
    }

    handleKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1);
    }

    move(direction) {
      const currentIndex = this.getCurrentIndex();
      const targetIndex = Math.min(this.cards.length - 1, Math.max(0, currentIndex + direction));
      const firstOffset = this.cards[0]?.offsetLeft || 0;
      const targetOffset = (this.cards[targetIndex]?.offsetLeft || firstOffset) - firstOffset;

      this.track.scrollTo({
        left: targetOffset,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    getCurrentIndex() {
      const firstOffset = this.cards[0]?.offsetLeft || 0;
      const currentPosition = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const relativeOffset = card.offsetLeft - firstOffset;
        const distance = Math.abs(relativeOffset - currentPosition);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    getVisibleCount() {
      if (this.cards.length < 2) return this.cards.length;

      const step = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
      if (step <= 0) return 1;

      return Math.max(1, Math.min(this.cards.length, Math.round(this.track.clientWidth / step)));
    }

    update() {
      if (!this.track || this.cards.length === 0) return;

      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const hasOverflow = maximumScroll > 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= maximumScroll - 1;
      const currentIndex = this.getCurrentIndex();
      const visibleCount = this.getVisibleCount();
      const visibleEnd = Math.min(this.cards.length, currentIndex + visibleCount);

      this.dataset.hasOverflow = String(hasOverflow);
      this.dataset.visibleStart = String(currentIndex + 1);
      this.dataset.visibleEnd = String(visibleEnd);

      if (this.previousButton) {
        this.previousButton.disabled = !hasOverflow || atStart;
        this.previousButton.setAttribute('aria-disabled', String(!hasOverflow || atStart));
      }

      if (this.nextButton) {
        this.nextButton.disabled = !hasOverflow || atEnd;
        this.nextButton.setAttribute('aria-disabled', String(!hasOverflow || atEnd));
      }
    }

    formatRelativeTimes() {
      const relativeFormatter = new Intl.RelativeTimeFormat('nl-NL', { numeric: 'always' });
      const dateFormatter = new Intl.DateTimeFormat('nl-NL', { dateStyle: 'long' });
      const now = Date.now();

      this.querySelectorAll('[data-ftr-time]').forEach((timeElement) => {
        const publishedAt = new Date(timeElement.dateTime);
        if (Number.isNaN(publishedAt.getTime())) return;

        const difference = publishedAt.getTime() - now;
        const absoluteDays = Math.abs(difference) / 86_400_000;
        let unit = 'day';
        let divisor = 86_400_000;

        if (absoluteDays < 1) {
          unit = 'hour';
          divisor = 3_600_000;
        } else if (absoluteDays >= 365) {
          unit = 'year';
          divisor = 31_556_952_000;
        } else if (absoluteDays >= 30) {
          unit = 'month';
          divisor = 2_629_746_000;
        }

        const value = Math.round(difference / divisor);
        timeElement.textContent = relativeFormatter.format(value, unit);
        timeElement.title = dateFormatter.format(publishedAt);
      });
    }
  }

  customElements.define('finch-trustpilot-reviews', FinchTrustpilotReviews);
}
